import { get_token, get_user_details } from "@/router";
import Swal from "sweetalert2";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  name: "CreateDocumentApproval",
  components: {
    Multiselect,
  },
  emits: ["temporary-document", "document-created"],
  data() {
    return ({
      user_details: {
        user_role: "",
        user_id: null,
      },
      reviewers: [],
      document_name: "",
      document_description: "",
      tempSelectedReviewer: [],
      selected_reviewers: [],
      fileName: "",
      filePreview: "",
      fileError: "",
      create_previews: [],
      create_upload_files: [],
      create_isDragging: false,
      upload_media: null,
      errors: {},
      isLoading: false,
      isSubmitting: false,
    });
  },
  computed: {
    portal_id() {
      return this.$route.params.portal_id;
    },
  },
  mounted() {
    this.user_details = get_user_details();

    const create_document_approval_modal = $("#document_approval_modal");
    create_document_approval_modal.on("show.bs.modal", () => {
      this.fetchPortalMembers();
    });
    create_document_approval_modal.on("hidden.bs.modal", () => {
      if (!this.isSubmitting) {
        this.clear_document_approval_modal();
      }
    });

    document
      .getElementById("document_approval_modal")
      ?.addEventListener("keydown", this.handleCreateApprovalKeydown, true);
  },
  beforeUnmount() {
    const create_document_approval_modal = $("#document_approval_modal");
    create_document_approval_modal.off("show.bs.modal");
    create_document_approval_modal.off("hidden.bs.modal");
    document
      .getElementById("document_approval_modal")
      ?.removeEventListener("keydown", this.handleCreateApprovalKeydown, true);
    this.revokeCreatePreviewUrls();
  },
  methods: {
    getToken() {
      return get_token();
    },
    get_editor_plain_text(editorRef) {
      const editor = editorRef?.getQuill?.() || editorRef?.getEditor?.();

      if (editor?.getText) {
        return editor.getText().trim();
      }

      if (typeof editorRef?.getText === "function") {
        return editorRef.getText().trim();
      }

      const fallbackText = document.createElement("div");
      fallbackText.textContent = this.document_description || "";
      return fallbackText.textContent.trim();
    },
    encode_description_text(description) {
      return String(description || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    scrollCreateApprovalModal(direction) {
      const modalBody = this.$refs.create_approval_modal_body;
      if (!modalBody) return;

      const scrollAmount = Math.max(modalBody.clientHeight * 0.8, 220);
      modalBody.scrollBy({
        top: direction === "up" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    },
    handleCreateApprovalKeydown(event) {
      if (!this.can_handle_create_approval_keydown(event)) return;

      event.preventDefault();
      this.scrollCreateApprovalModal(this.get_create_approval_scroll_direction(event));
    },
    can_handle_create_approval_keydown(event) {
      return this.is_create_approval_scroll_key(event) && this.create_approval_modal_is_open();
    },
    is_create_approval_scroll_key(event) {
      return ["ArrowDown", "ArrowUp"].includes(event.key);
    },
    create_approval_modal_is_open() {
      const modalEl = document.getElementById("document_approval_modal");
      return modalEl?.classList.contains("show");
    },
    get_create_approval_scroll_direction(event) {
      return event.key === "ArrowUp" ? "up" : "down";
    },
    scrollToCreateApprovalError() {
      const modalBody = this.$refs.create_approval_modal_body;
      if (!modalBody) return;

      const errorTargets = [
        {
          key: "document_name",
          section: this.$refs.documentNameSection,
          focus: () => document.getElementById("create_document_name")?.focus(),
        },
        {
          key: "reviewer",
          section: this.$refs.reviewerSection,
          focus: () => this.$refs.reviewerSection?.querySelector("input")?.focus(),
        },
        {
          key: "upload_media",
          section: this.$refs.upload_media_section,
          focus: () => this.$refs.approvalFileInput?.focus(),
        },
      ];

      const target = errorTargets.find(
          ({ key, section }) => Reflect.get(this.errors, key) && section
      );
      if (!target) return;

      const top =
        target.section.offsetTop -
        modalBody.offsetTop -
        12;

      modalBody.scrollTo({
        top: Math.max(top, 0),
        behavior: "smooth",
      });

      window.setTimeout(() => target.focus?.(), 250);
    },
    validate_document_form() {
      this.errors = {};

      if (!this.document_name.trim()) {
        this.errors.document_name = "Document name is required.";
      }

      if (!this.tempSelectedReviewer || this.tempSelectedReviewer.length === 0) {
        this.errors.reviewer = "At least one reviewer must be selected.";
      }



      const isValid = Object.keys(this.errors).length === 0;

      if (!isValid) {
        this.$nextTick(() => {
          this.scrollToCreateApprovalError();
        });
      }

      return isValid;
    },
    handleApprovalDrop(event) {
      this.create_isDragging = false;
      const files = Array.from(event.dataTransfer.files || []);
      this.addApprovalPreviewFiles(files);
    },
    handleApprovalFileUpload(event) {
      const files = Array.from(event.target.files || []);
      this.addApprovalPreviewFiles(files);
      event.target.value = "";
    },
    addApprovalPreviewFiles(files = []) {
      if (!files.length) return;

      this.fileError = "";
      const MAX_FILES = 10;

      if (files.length + this.create_upload_files.length > MAX_FILES) {
        this.fileError = `Maximum ${MAX_FILES} files allowed.`;
        return;
      }

      files.forEach((file) => {
        this.create_upload_files.push(file);
        this.upload_media = file;
        this.fileName = file.name;

        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.create_previews.push({
              type: "image",
              src: e.target.result,
              name: file.name,
            });
          };
          reader.readAsDataURL(file);
        } else if (file.type.startsWith("video/")) {
          this.create_previews.push({
            type: "video",
            src: URL.createObjectURL(file),
            name: file.name,
          });
        } else {
          this.create_previews.push({
            type: "file",
            name: file.name,
            extension: file.name.split(".").pop().toLowerCase(),
          });
        }
      });
    },
    removeApprovalFile(index) {
      const [preview] = this.create_previews.splice(index, 1);
      this.create_upload_files.splice(index, 1);

      if (preview?.src && preview.type === "video") {
        URL.revokeObjectURL(preview.src);
      }

      const lastFile = this.create_upload_files[this.create_upload_files.length - 1] || null;
      this.fileName = lastFile?.name || "";
      this.upload_media = lastFile;
    },
    revokeCreatePreviewUrls() {
      this.create_previews.forEach((preview) => {
        if (preview?.src && preview.type === "video") {
          URL.revokeObjectURL(preview.src);
        }
      });
    },
    clear_document_approval_modal() {
      this.document_name = "";
      this.document_description = "";
      this.tempSelectedReviewer = [];
      this.fileName = "";
      this.filePreview = "";
      this.fileError = "";
      this.upload_media = null;
      this.revokeCreatePreviewUrls();
      this.create_previews = [];
      this.create_upload_files = [];
      this.create_isDragging = false;
      this.errors = {};

      const fileInput = this.$refs.approvalFileInput || document.getElementById("approvalFileInput");
      if (fileInput) {
        fileInput.value = "";
      }

      const editorRef = this.$refs.createQuillDocumentEditorRef;
      const editor = editorRef?.getQuill?.() || editorRef?.getEditor?.();
      if (editor?.setText) {
        editor.setText("");
      } else if (editor?.root) {
        editor.root.innerHTML = "";
      } else if (editorRef?.setHTML) {
        editorRef.setHTML("");
      }
    },
    fetchPortalMembers() {
      if (this.reviewers.length > 0) return;

      this.isLoading = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_portal_members/`,
        method: "GET",
        data: { portal_id: this.portal_id },
        headers: {
          Authorization: `Token ${this.getToken()}`,
          "Content-Type": "application/json",
        },
        success: (response) => {
          if (response.status === "success") {
            const currentUserId = Number(this.user_details.user_id);
            this.reviewers = response.data.members
              .filter((member) => member.user && member.user.id !== currentUserId)
              .map((member) => ({
                portal_access_id: member.id,
                user: member.user,
                first_name: member.user.first_name,
                last_name: member.user.last_name,
                full_name: `${member.user.first_name} ${member.user.last_name}`,
              }));
          }
        },
        error: (xhr) => {
          console.error("Error fetching portal members:", xhr);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
    },
    createDocumentApproval() {
      if (!this.validate_document_form()) {
        return;
      }

      this.$emit("temporary-document", {
        document_name: this.document_name,
        isTemp: true,
      });

      $("#document_approval_modal").modal("hide");

      this.$nextTick(() => {
        const tempPostElement = document.getElementById("temp-post");
        if (tempPostElement) {
          tempPostElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });

      this.isSubmitting = true;
      const formData = new FormData();
      formData.append("document_name", this.document_name);

      const documentDescription = this.encode_description_text(
        this.get_editor_plain_text(this.$refs.createQuillDocumentEditorRef)
      );
      formData.append("description", documentDescription);
      formData.append("portal_id", this.portal_id);

      this.selected_reviewers = this.tempSelectedReviewer
        .map((reviewer) => reviewer.portal_access_id || reviewer.id)
        .filter(Boolean);

      this.selected_reviewers.forEach((id) => {
        formData.append("reviewer", id);
      });

      const baseUrl = `${window.location.origin}${this.$route.path}`;
      const documentUrl = `${baseUrl}?portal_id=${this.portal_id}`;
      formData.append("base_url", documentUrl);

      this.create_upload_files.forEach((file) => {
        formData.append("file", file);
      });

      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/request_document_approval/`,
        data: formData,
        processData: false,
        contentType: false,
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        success: (response) => {
          if (response.status === "success") {
            this.$emit("document-created", response.data);

            this.$nextTick(() => {
              const newPostElement = document.getElementById(`post-${response.data.id}`);
              if (newPostElement) {
                newPostElement.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            });

            this.clear_document_approval_modal();
          }
        },
        error: (response) => {
          Swal.close();
          const response_data = response.responseJSON;
          if (response_data?.status === "error") {
            this.errors.document_name = response_data.message;
          }
        },
        complete: () => {
          this.isSubmitting = false;
        },
      });
    },
  },
};
